/**
 * Sales Account Owner + Concierge rep for a business (Business Account Pages slice 2, migration 2026-10-06-313).
 *  - Sales Account Owner: chosen from ACTIVE sales positions; saved as the primary sales_territory_assignments row.
 *  - Concierge rep: chosen from the Concierge team roster (requirements §6.7). The roster is internal staff an
 *    administrator puts on the Concierge team; when it is empty the picker says so instead of offering nothing.
 */

import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Card from "../ui/Card";
import Modal from "../ui/Modal";
import Button from "../common/Button";
import { useToast } from "../ui/Toast";
import {
  getActiveSalesPositions,
  getConciergeTeam,
  getOrganizationConcierge,
  getOrganizationSalesOwner,
  listConciergeCandidates,
  salesPositionLabel,
  setConciergeTeamMember,
  setOrganizationConcierge,
  setOrganizationSalesOwner,
} from "../../services/organizationPeople.service";

interface Props {
  organizationId: string;
  canEdit: boolean;
  /** administrators may edit the Concierge team roster */
  canManageTeam: boolean;
}

const describe = (m?: string) => {
  if (!m) {
    return "Could not save.";
  }
  if (m.includes("OWNER_INVALID")) {
    return "Choose an active sales position.";
  }
  if (m.includes("NOT_ALLOWED") || m.includes("row-level security")) {
    return "You do not have permission to change this.";
  }
  if (m.includes("CONCIERGE_INVALID")) {
    return m.replace(/^.*CONCIERGE_INVALID:\s*/, "");
  }
  if (m.includes("foreign key")) {
    return "That person is no longer on the Concierge team. Reload and choose again.";
  }
  return m;
};

const ConciergeTeamModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { data: people = [], isLoading } = useQuery({
    queryKey: ["concierge-candidates"],
    queryFn: listConciergeCandidates,
    enabled: isOpen,
  });
  const toggle = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) =>
      setConciergeTeamMember(id, on),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["concierge-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["concierge-team"] });
      queryClient.invalidateQueries({ queryKey: ["organization-concierge"] });
    },
    onError: (e: Error) => showToast("error", describe(e.message)),
  });
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Concierge team" size="md">
      <p className="text-sm text-gray-600 mb-3">
        Tick the internal users who are Concierge reps. Only people on this team
        can be assigned to a business. Removing someone also clears the
        businesses they were assigned to.
      </p>
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="max-h-80 overflow-y-auto divide-y border rounded">
          {people.map((p) => (
            <label
              key={p.profile_id}
              className="flex items-center gap-3 p-2 text-sm"
            >
              <input
                type="checkbox"
                checked={p.on_team}
                disabled={toggle.isPending}
                onChange={(e) =>
                  toggle.mutate({ id: p.profile_id, on: e.target.checked })
                }
              />
              <span>
                {p.full_name || p.email}
                <span className="text-gray-500">
                  {" "}
                  · {p.role.replace("_", " ")}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
      <div className="flex justify-end pt-4">
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </div>
    </Modal>
  );
};

const OrganizationAssignmentsCard: React.FC<Props> = ({
  organizationId,
  canEdit,
  canManageTeam,
}) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [teamOpen, setTeamOpen] = useState(false);

  const { data: positions = [] } = useQuery({
    // Distinct key per data shape (2026-10-16): the same key held different rows/columns on other
    // screens, and with the 5-minute staleTime whichever screen fetched first won.
    queryKey: ["sales-positions-active", "org-assignments"],
    queryFn: getActiveSalesPositions,
  });
  const { data: owner, isLoading: ownerLoading } = useQuery({
    queryKey: ["organization-sales-owner", organizationId],
    queryFn: () => getOrganizationSalesOwner(organizationId),
  });
  const { data: team = [] } = useQuery({
    queryKey: ["concierge-team"],
    queryFn: getConciergeTeam,
  });
  const { data: conciergeId, isLoading: conciergeLoading } = useQuery({
    queryKey: ["organization-concierge", organizationId],
    queryFn: () => getOrganizationConcierge(organizationId),
  });

  const saveOwner = useMutation({
    mutationFn: (id: string | null) =>
      setOrganizationSalesOwner(organizationId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organization-sales-owner", organizationId],
      });
      showToast("success", "Sales account owner saved");
    },
    onError: (e: Error) => showToast("error", describe(e.message)),
  });
  const saveConcierge = useMutation({
    mutationFn: (id: string | null) =>
      setOrganizationConcierge(organizationId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organization-concierge", organizationId],
      });
      showToast("success", "Concierge rep saved");
    },
    onError: (e: Error) => showToast("error", describe(e.message)),
  });

  const ownerInList = owner && positions.some((p) => p.id === owner.id);
  const assigned = team.find((m) => m.profile_id === conciergeId);
  const selectCls =
    "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50";

  return (
    <Card>
      <div
        className="flex items-center justify-between mb-4"
        data-testid="section-assignments"
      >
        <h3 className="text-lg font-semibold text-gray-900">
          Ownership & Servicing
        </h3>
        {canManageTeam && (
          <Button variant="ghost" size="sm" onClick={() => setTeamOpen(true)}>
            Manage Concierge team
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="sales-owner"
            className="block text-sm text-gray-600 mb-1"
          >
            Sales Account Owner
          </label>
          <select
            id="sales-owner"
            data-testid="sales-owner-select"
            className={selectCls}
            disabled={!canEdit || ownerLoading || saveOwner.isPending}
            value={owner?.id ?? ""}
            onChange={(e) => saveOwner.mutate(e.target.value || null)}
          >
            <option value="">Not assigned</option>
            {owner && !ownerInList && (
              <option value={owner.id}>
                {salesPositionLabel(owner)} (inactive)
              </option>
            )}
            {positions.map((p) => (
              <option key={p.id} value={p.id}>
                {salesPositionLabel(p)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Field sales rep of record (active sales positions).
          </p>
        </div>
        <div>
          <label
            htmlFor="concierge-rep"
            className="block text-sm text-gray-600 mb-1"
          >
            Concierge Rep
          </label>
          {team.length === 0 && !conciergeId ? (
            <p
              className="rounded border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-500"
              data-testid="concierge-none-available"
            >
              None available — no one is on the Concierge team yet
              {canManageTeam
                ? ". Use “Manage Concierge team” to add people."
                : ". Ask an administrator to set it up."}
            </p>
          ) : (
            <select
              id="concierge-rep"
              data-testid="concierge-select"
              className={selectCls}
              disabled={!canEdit || conciergeLoading || saveConcierge.isPending}
              value={conciergeId ?? ""}
              onChange={(e) => saveConcierge.mutate(e.target.value || null)}
            >
              <option value="">Not assigned</option>
              {conciergeId && !assigned && (
                <option value={conciergeId}>(assigned rep)</option>
              )}
              {team.map((m) => (
                <option key={m.profile_id} value={m.profile_id}>
                  {m.full_name || m.email}
                </option>
              ))}
            </select>
          )}
          <p className="mt-1 text-xs text-gray-500">
            From the Concierge Care team roster.
          </p>
        </div>
      </div>
      {canManageTeam && (
        <ConciergeTeamModal
          isOpen={teamOpen}
          onClose={() => setTeamOpen(false)}
        />
      )}
    </Card>
  );
};

export default OrganizationAssignmentsCard;
